import { cast, detach, flow, toGenerator, types, getSnapshot, SnapshotIn, SnapshotOut } from 'mobx-state-tree'
import { OperationFailure, OperationInProgress, OperationNotStarted, OperationStatus, OperationStatusModel, OperationSuccess } from '../models/common/OperationStatus'
import { FetchModel } from '../models/FetchModel'
import { VehicleMaintenance, VehicleMaintenanceModel } from '../models/Vehicles/VehicleMaintenanceModel'
import { 
    ExistingVehicleEditingCloneModel, 
    ExistingVehicleModel, 
    ExistingVehicleWithChecklistInstancesSnapshot, 
    NewVehicleEditCloneModel, 
    VehicleEditCloneModel } from '../models/Vehicles/VehicleModel'
import { VehicleModuleVersion, VehicleModuleVersionModel } from '../models/Vehicles/VehicleModuleVersionModel'
import { VehicleStateModel, VehicleStateUpdate } from '../models/Vehicles/VehicleStateModel'
import { ExistingPartInstanceEditingCloneModel, ExistingPartInstanceFromBackendModel, ExistingPartInstanceModel, NewPartInstanceEditingCloneModel, PartInstanceActionLog, PartInstanceActionLogModel, PartInstanceEditingCloneModel } from '../models/Vehicles/PartInstanceModel'

export const VehiclesStore = FetchModel
.named('VehiclesStore')
.props({
    vehicles: types.maybeNull(types.array(ExistingVehicleModel)),
    // We hold states in a separate map, and not in the vehicle objects, since vehicle state updates come via the websocket, 
    // and a vehicle status update might arrive before we fetched this vehicle from the backend (as in the case of new vehicle creation, for example)
    vehicleStates: types.map(VehicleStateModel), 

    vehicleCloneForEditing: types.maybeNull(VehicleEditCloneModel),

    selectedVehicle: types.maybeNull(types.reference(ExistingVehicleModel)),
    selectedPartInstance: types.maybeNull(types.reference(ExistingPartInstanceModel)),
    
    editedPartInstance: types.maybeNull(PartInstanceEditingCloneModel),

    fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    saveStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    changeIsParkedStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    changeInServiceStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    // map of vehicle id to its maintenance status (health of part instances and unresolved discrepancy reports)
    vehicleMaintenance: types.map(VehicleMaintenanceModel),
    fetchVehicleMaintenanceStatuses: types.map(OperationStatusModel),
    fetchVehiclesStateStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    // map of vehicle id to module version information
    vehicleModuleVersionInformation: types.map(types.array(VehicleModuleVersionModel)),
    fetchModuleVersionInfoStatuses: types.map(OperationStatusModel),
    
    // map of vehicle id to its parts
    vehicleParts: types.map(types.array(ExistingPartInstanceModel)),
    fetchVehiclePartsStatuses: types.map(OperationStatusModel),

    // part of part id to its action logs
    partInstanceLogs: types.map(types.array(PartInstanceActionLogModel)),
    fetchPartInstanceLogStatuses: types.map(OperationStatusModel),

    markPartAsUnAirworthyStatuses: types.map(OperationStatusModel),
    markPartAsInspectedStatuses: types.map(OperationStatusModel),
    removePartFromVehicleStatuses: types.map(OperationStatusModel),

    saveEditedPartInstanceStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    unassignedPartInstances: types.maybeNull(types.array(ExistingPartInstanceModel)),
    fetchUnassignedPartInstancesStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
})
.views((self) => ({
    get getVehiclesUrl(): string {
        return `${self.baseUrl}vehicles/`
    },
    get getVehiclesStateUrl(): string {
        return `${self.baseUrl}vehicles_state/`
    },
    vehicleUrl(vehicleId: number): string {
        return `${self.baseUrl}vehicles/${vehicleId.toString()}/`
    },
    get getVehicleMaintenanceUrl(): string {
        return `${self.baseUrl}vehicles_maintenance/`
    },
    getVehicleVersionInfoUrl(vehicleId: number) {
        return `${self.baseUrl}vehicles/${vehicleId.toString()}/all_module_versions/`
    },
    getVehicleSetParkedStateUrl(vehicleId: number) {
        return `${self.baseUrl}vehicles/${vehicleId.toString()}/set_vehicle_parking_state/`
    },
    getVehicleSetServiceStateUrl(vehicleId: number) {
        return `${self.baseUrl}vehicles/${vehicleId.toString()}/set_vehicle_service_state/`
    },
    vehiclePartsUrl(vehicleId: number) {
        return `${self.baseUrl}vehicles/${vehicleId.toString()}/parts/`
    },
    get getPartInstancesUrl() : string {
        return `${self.baseUrl}part_instances/`
    },
    get getUnassignedPartInstancesUrl() : string {
        return `${self.baseUrl}part_instances/unassigned_part_instances/`
    },
    getPartInstanceUrl(partId: number) {
        return `${self.baseUrl}part_instances/${partId.toString()}/`
    },
    getPartActionLogUrl(partId: number) {
        return `${self.baseUrl}part_instances/${partId.toString()}/logs/`
    },
    getMarkPartAsUnairworthyUrl(partId: number) {
        return `${self.baseUrl}part_instances/${partId.toString()}/mark_as_unairworthy/`
    },
    getMarkPartAsInspectedUrl(partId: number) {
        return `${self.baseUrl}part_instances/${partId.toString()}/perform_maintenance/`
    },
}))
.actions(self => ({
    fetchUnassignedPartInstances: flow(function* fetchUnassignedPartInstances() {
        self.fetchUnassignedPartInstancesStatus = OperationInProgress()

        const fetchUnassignedPartInstancesResult = yield* toGenerator(self.loadObjectOrError<Array<SnapshotIn<typeof ExistingPartInstanceFromBackendModel>>>(
            self.getUnassignedPartInstancesUrl,
            { method: 'POST' },
            { fetchOperationName: 'fetch unassigned part instances'}
        ))

        if (fetchUnassignedPartInstancesResult.error) {
            self.fetchUnassignedPartInstancesStatus = OperationFailure(fetchUnassignedPartInstancesResult.error)
            return
        }

        const unassignedParts = fetchUnassignedPartInstancesResult.result.map(partInstanceSnapshot => ExistingPartInstanceFromBackendModel.create(partInstanceSnapshot))
        self.unassignedPartInstances = cast(unassignedParts.map(x => getSnapshot(x)))
        
        self.fetchUnassignedPartInstancesStatus = OperationSuccess()
    }),

}))
.actions((self) => {
    // Special method to update the statuses map.
    // We explicitly detach the previous statuses. This is done to allow access to these objects (otherwise mobx-state-tree throws an error regarding access to a node which is not in the tree)
    const _updateOperationStatusInMap = (map: typeof self.fetchModuleVersionInfoStatuses, key: string, status: OperationStatus) => {
        const oldStatus = map.get(key) 
        if (oldStatus !== undefined) {
            detach(oldStatus)
        }
        map.set(key, status)
    }

    const _updateFetchModuleVersionsStatus = (vehicleId: number, status: OperationStatus) => {
        _updateOperationStatusInMap(self.fetchModuleVersionInfoStatuses, vehicleId.toString(), status)
    }
    const _updateFetchPartsStatus = (vehicleId: number, status: OperationStatus) => {
        _updateOperationStatusInMap(self.fetchVehiclePartsStatuses, vehicleId.toString(), status)
    }

    const _updateFetchPartLogsStatus = (partInstanceId: number, status: OperationStatus) => {
        _updateOperationStatusInMap(self.fetchPartInstanceLogStatuses, partInstanceId.toString(), status)
    }
    const _updateMarkAsUnAirworthyStatus = (partInstanceId: number, status: OperationStatus) => {
        _updateOperationStatusInMap(self.markPartAsUnAirworthyStatuses, partInstanceId.toString(), status)
    }
    const _updateFetchMaintenanceStatus = (partInstanceId: number, status: OperationStatus) => {
        _updateOperationStatusInMap(self.fetchVehicleMaintenanceStatuses, partInstanceId.toString(), status)
    }
    const _updateMarkAsInspectedStatus = (partInstanceId: number, status: OperationStatus) => {
        _updateOperationStatusInMap(self.markPartAsInspectedStatuses, partInstanceId.toString(), status)
    }
    const _updateRemovePartFromVehicleStatus = (partInstanceId: number, status: OperationStatus) => {
        _updateOperationStatusInMap(self.removePartFromVehicleStatuses, partInstanceId.toString(), status)
    }
    const fetchVehicleParts = flow(function* fetchVehicleParts(vehicleId: number) {
        _updateFetchPartsStatus(vehicleId, OperationInProgress())

        const fetchVehiclePartsResult = yield* toGenerator(
            self.loadObjectOrError<Array<SnapshotIn<typeof ExistingPartInstanceFromBackendModel>>>(
                self.vehiclePartsUrl(vehicleId),
                { method: 'POST' },
                { fetchOperationName: 'fetch vehicle parts'}
            )
        )

        if (fetchVehiclePartsResult.error) {
            _updateFetchPartsStatus(vehicleId, OperationFailure(fetchVehiclePartsResult.error))
            return
        }

        const partInstances = fetchVehiclePartsResult.result.map(partInstanceSnapshot => ExistingPartInstanceFromBackendModel.create(partInstanceSnapshot))
        self.vehicleParts.set(vehicleId.toString(), (partInstances.map(x => getSnapshot(x))).sort((p1,p2) => (p1.id > p2.id ? 1 : -1))) // sorting the part according to ids to make the list stable when displayed
         // TODO: right now we convert to and from snapshot again to overcome the typescript error. Should find a workaround or get rid of the snapshotProcessor
        _updateFetchPartsStatus(vehicleId, OperationSuccess())
    })
    const fetchVehicleMaintenanceStatus = flow(function* fetchVehicleMaintenanceStatus(vehicleId: number) {
        _updateFetchMaintenanceStatus(vehicleId, OperationInProgress())
        // Currently, there's no API endpoint to fetch maintenance status of a single vehicle... so we'll get them all and select the one we need.
        const loadVehicleMaintenanceResult = yield* toGenerator(self.loadObjectOrError<Array<SnapshotIn<VehicleMaintenance>>>(
            self.getVehicleMaintenanceUrl,
            {},
            { fetchOperationName: 'Fetch vehicles maintenance' }
        ))

        if (loadVehicleMaintenanceResult.error) {
            _updateFetchMaintenanceStatus(vehicleId, OperationFailure(loadVehicleMaintenanceResult.error))
            return
        }
    
        // Find maintenance info of the vehicle we are trying to refresh
        const givenVehicleMaintenance = loadVehicleMaintenanceResult.result.find(maintenance => maintenance.vehicle_id === vehicleId)
        if (givenVehicleMaintenance === undefined) {
            _updateFetchMaintenanceStatus(vehicleId, OperationFailure({ name: 'ApplicationError', message: `Maintenance status of vehicle ${vehicleId} not found`}))
            return
        }
        self.vehicleMaintenance.set(vehicleId.toString(), givenVehicleMaintenance)
        _updateFetchMaintenanceStatus(vehicleId, OperationSuccess())
    })
    const fetchPartInstanceLogs = flow(function* fetchPartInstanceLogs(partInstanceId: number) {
        _updateFetchPartLogsStatus(partInstanceId, OperationInProgress())
        
        const fetchPartLogsResult = yield* toGenerator(self.loadObjectOrError<Array<SnapshotIn<PartInstanceActionLog>>>(
            self.getPartActionLogUrl(partInstanceId),
            { method: 'POST' },
            { fetchOperationName: 'Fetch part instance action logs'}
        ))

        if (fetchPartLogsResult.error) {
            _updateFetchPartLogsStatus(partInstanceId, OperationFailure(fetchPartLogsResult.error))
            return
        }

        self.partInstanceLogs.set(partInstanceId.toString(), fetchPartLogsResult.result)

        _updateFetchPartLogsStatus(partInstanceId, OperationSuccess())
    })
    return {
        fetchVehicleModuleVersions: flow(function* fetchVehicleModuleVersions(vehicleId: number) {
            _updateFetchModuleVersionsStatus(vehicleId, OperationInProgress()) 

            const fetchVersionInfoResult = yield* toGenerator(
                self.loadObjectOrError<Array<SnapshotIn<VehicleModuleVersion>>>(
                    self.getVehicleVersionInfoUrl(vehicleId),
                    { method: 'POST' },
                    { fetchOperationName: 'fetch vehicle modules version info'}
                )
            )

            if (fetchVersionInfoResult.error) {
                _updateFetchModuleVersionsStatus(vehicleId, OperationFailure(fetchVersionInfoResult.error))
                return
            }

            self.vehicleModuleVersionInformation.set(vehicleId.toString(), fetchVersionInfoResult.result)
            _updateFetchModuleVersionsStatus(vehicleId, OperationSuccess()) 
        }),
        fetchVehicleParts,
        fetchPartInstanceLogs,
        markPartAsUnAirworthy: flow(function* markPartAsUnAirworthy(vehicleId: number, partId: number) {
            _updateMarkAsUnAirworthyStatus(partId, OperationInProgress())

            const markAsUnairworthyResult = yield* toGenerator(
                self.loadObjectOrError<void>(
                    self.getMarkPartAsUnairworthyUrl(partId), 
                    { method: 'POST' },
                    { fetchOperationName: 'Mark part as unairworthy'}
                )
            )
            if (markAsUnairworthyResult.error) {
                _updateMarkAsUnAirworthyStatus(partId, OperationFailure(markAsUnairworthyResult.error))
                return
            }
            
            _updateMarkAsUnAirworthyStatus(partId, OperationSuccess())
            self.selectedPartInstance = null
            // refresh vehicle maintenance status and parts
            void fetchVehicleParts(vehicleId)
            void fetchVehicleMaintenanceStatus(vehicleId)
            void fetchPartInstanceLogs(partId)
        }),
        markPartAsInspected: flow(function* markPartAsInspected(vehicleId: number, partId: number) {
            _updateMarkAsInspectedStatus(partId, OperationInProgress())

            const markAsInspectedResult = yield* toGenerator(
                self.loadObjectOrError<void>(
                    self.getMarkPartAsInspectedUrl(partId), 
                    { method: 'POST' },
                    { fetchOperationName: 'Mark part as inspected'}
                )
            )
            if (markAsInspectedResult.error) {
                _updateMarkAsInspectedStatus(partId, OperationFailure(markAsInspectedResult.error))
                return
            }
            
            _updateMarkAsInspectedStatus(partId, OperationSuccess())
            
            // refresh vehicle maintenance status and parts
            void fetchVehicleParts(vehicleId)
            void fetchVehicleMaintenanceStatus(vehicleId)
            void fetchPartInstanceLogs(partId)
        }),
        removePartFromVehicle: flow(function* removePartFromVehicle(vehicleId: number, partId: number) {
            _updateRemovePartFromVehicleStatus(partId, OperationInProgress())

            const removePartResult = yield* toGenerator(
                self.loadObjectOrError<void>(
                    self.getPartInstanceUrl(partId), 
                    { method: 'PATCH', body: JSON.stringify({ vehicle_id: null })},
                    { fetchOperationName: 'Mark part as inspected'}
                )
            )
            if (removePartResult.error) {
                _updateRemovePartFromVehicleStatus(partId, OperationFailure(removePartResult.error))
                return
            }
            
            _updateRemovePartFromVehicleStatus(partId, OperationSuccess())
            self.selectedPartInstance = null
            // refresh vehicle maintenance status and parts
            void fetchVehicleParts(vehicleId)
            void fetchVehicleMaintenanceStatus(vehicleId)
            void self.fetchUnassignedPartInstances()
        }),
    }
})
.actions((self) => ({
    clearSelectedVehicle() {
        self.selectedVehicle = null
        self.vehicleCloneForEditing = null
        self.selectedPartInstance = null
        self.editedPartInstance = null
    },
    setSelectedVehicle(vehicleId: number): boolean {
        if (self.vehicles === null) {
            throw Error("Can't set selected vehicle before fetching completed")
        }
        const selectedVehicle = self.vehicles.find(vehicle => vehicle.id === vehicleId) 
        if (selectedVehicle === undefined) {
            console.warn(`Vehicle with id ${vehicleId} not found`)
            return false
        }
        self.selectedPartInstance = null
        self.editedPartInstance = null
        self.selectedVehicle = selectedVehicle
        
        const vehicleSnapshot = getSnapshot(selectedVehicle)
        self.vehicleCloneForEditing = ExistingVehicleEditingCloneModel.create(vehicleSnapshot)

        return true
    },
    setSelectedPart(partId: number) {
        if (self.vehicles === null) {
            throw Error("Can't set selected vehicle part before fetching completed")
        }
        if (self.selectedVehicle === null) {
            throw Error("Can't set selected vehicle part, no vehicle selected")
        }
        if (!self.vehicleParts.has(self.selectedVehicle.id.toString())) {
            throw Error("Can't set selected vehicle part, parts for vehicle not fetched")
        }
        const selectedVehicleId = self.selectedVehicle.id
        const part = self.vehicleParts.get(selectedVehicleId.toString())?.find(
            part => part.id === partId
        )
        if (part === undefined) {
            console.warn(`Part with id ${partId} not found in vehicle ${self.selectedVehicle.id}`)
            return
        }
        self.selectedPartInstance = part
        self.editedPartInstance = null
        void self.fetchPartInstanceLogs(partId)
    },
    createNewVehicle() {
        self.vehicleCloneForEditing = NewVehicleEditCloneModel.create({
            name: '',
            center_id: 1, // TODO: get from some other place, like commonSettingsStore
        })
        self.selectedVehicle = null
        self.selectedPartInstance = null
        self.editedPartInstance = null
    },
    createNewPartInstance() {
        if (self.selectedVehicle === null) {
            console.error('Cannot create a new part instance for vehicle when no vehicle is selected')
            return
        }

        self.editedPartInstance = NewPartInstanceEditingCloneModel.create({ vehicle_id: self.selectedVehicle.id })
    },
    editPartInstanceOfSelectedVehicle(partId: number) {
        if (self.selectedVehicle === null) {
            console.warn(`No Vehicle selected, cannot edit part`)
            return
        }
        const partInstance = self.vehicleParts.get(self.selectedVehicle.id.toString())?.find(partInstance => partInstance.id === partId)
        if (partInstance === undefined) {
            console.warn(`Part instance with id ${partId} not found`)
            return
        }
        const partInstanceSnapshot = getSnapshot(partInstance)
        
        self.editedPartInstance = ExistingPartInstanceEditingCloneModel.create(partInstanceSnapshot)
    },
}))
.actions((self) => ({
    _updateVehicleState(stateUpdate: VehicleStateUpdate) {
        const { id: vehicleId, ...vehicleStatus } = stateUpdate
        const prevStatus = self.vehicleStates.get(vehicleId.toString())
        if (prevStatus !== undefined) {
            detach(prevStatus)
        }

        const newStatus = VehicleStateModel.create(vehicleStatus)
        self.vehicleStates.set(vehicleId.toString(), newStatus)
    },
    _onWebSocketDisconnected() {
        self.vehicleStates.clear()
    },
    fetchVehiclesState: flow(function* fetchVehiclesState() {
        self.fetchVehiclesStateStatus = OperationInProgress()

        const loadVehiclesStateResult = yield* toGenerator(self.loadObjectOrError<Array<VehicleStateUpdate>>(
            self.getVehiclesStateUrl,
            {},
            { fetchOperationName: 'Fetch vehicles state' }
        ))
        
        if (loadVehiclesStateResult.error) {
            self.fetchVehiclesStateStatus = OperationFailure(loadVehiclesStateResult.error)
            return
        }

        const vehiclesStateResult = loadVehiclesStateResult.result
        const vehiclesStatesAsMap = vehiclesStateResult.reduce((map, vehicleState) => ( map[vehicleState.id] = vehicleState, map ), {})
        self.vehicleStates = cast(vehiclesStatesAsMap)
        self.fetchVehiclesStateStatus = OperationSuccess()

    }),
}))
.actions((self) => ({
    fetchAndSetVehiclesAndMaintenance: flow(function* fetchAndSetVehicles() {
        self.fetchStatus = OperationInProgress()

        const selectedVehicleId = self.selectedVehicle?.id
        const loadVehiclesResult = yield* toGenerator(self.loadObjectOrError<Array<ExistingVehicleWithChecklistInstancesSnapshot>>(
            self.getVehiclesUrl,
            {},
            { fetchOperationName: 'Fetch vehicles' }
        ))

        if (loadVehiclesResult.error) {
            self.fetchStatus = OperationFailure(loadVehiclesResult.error)
            return
        }
        
        const loadVehicleMaintenanceResult = yield* toGenerator(self.loadObjectOrError<Array<SnapshotIn<VehicleMaintenance>>>(
            self.getVehicleMaintenanceUrl,
            {},
            { fetchOperationName: 'Fetch vehicles maintenance' }
        ))

        if (loadVehicleMaintenanceResult.error) {
            self.fetchStatus = OperationFailure(loadVehicleMaintenanceResult.error)
            return
        }

        self.clearSelectedVehicle() // clear reference to old selected vehicle, if exists

        // Replace checklist objects with just the checklist ids for the references to work.
        const vehiclesFromBackend = loadVehiclesResult.result.map(vehicleWithChecklists => ({
            ...vehicleWithChecklists,
            checklists: vehicleWithChecklists.checklists.map(checklist => checklist.id),
        }))
        self.vehicles = cast(vehiclesFromBackend)
        
        // convert array to map taken from here: https://stackoverflow.com/a/44325124/4574731
        const vehicleMaintenanceAsMap = loadVehicleMaintenanceResult.result.reduce((map, maintenanceInfo) => (map[maintenanceInfo.vehicle_id] = maintenanceInfo, map), {})
    
        self.vehicleMaintenance = cast(vehicleMaintenanceAsMap)
        if (selectedVehicleId !== undefined) {
            void self.setSelectedVehicle(selectedVehicleId)
        }

        self.fetchStatus = OperationSuccess()
        void self.fetchVehiclesState()
        void self.fetchUnassignedPartInstances()
        
    }),
    setEditedPartInstanceFromSerial(serial: string) {
        if (self.unassignedPartInstances === null || self.selectedVehicle === null) {
            return
        }

        const unassignedPartInstanceWithGivenSerial = self.unassignedPartInstances.find(partInstance => partInstance.serial === serial)
        if (unassignedPartInstanceWithGivenSerial === undefined) {
            // If the current editedPartInstance was set from unassignedPartInstances, and now we don't find a candidate - go back to a new part instance.
            if (ExistingPartInstanceEditingCloneModel.is(self.editedPartInstance) && 
            self.unassignedPartInstances.map(part => part.id).includes(self.editedPartInstance.id)) {
                self.createNewPartInstance()
                self.editedPartInstance.serial = serial
            }
            return
        }   

        self.editedPartInstance = ExistingPartInstanceEditingCloneModel.create({
            ...getSnapshot(unassignedPartInstanceWithGivenSerial),
            vehicle_id: self.selectedVehicle.id
        })
    },
}))
.views((self) => ({
    get canToggleSelectedVehicleIsParked() {
        if (self.selectedVehicle === null) {
            return false
        }
        return self.vehicleStates.has(self.selectedVehicle.id.toString())
    },
    get canToggleSelectedVehicleInService() {
        if (self.selectedVehicle === null) {
            return false
        }
        return self.vehicleStates.has(self.selectedVehicle.id.toString())
    },
}))
.views((self) => ({
    get isAddingPartInstance() {
        return ExistingPartInstanceEditingCloneModel.is(self.editedPartInstance)
    },
  }))
.actions((self) => {
    function replaceChecklistReferences(snapshotWithChecklistReferences: SnapshotOut<typeof VehicleEditCloneModel>) {
        return {
            ...snapshotWithChecklistReferences,
            checklists: snapshotWithChecklistReferences.checklists.map(checklistId => ({id: (checklistId as number)})),
        }
    }
    return {
        saveEditedVehicle: flow(function* saveEditedVehicle() {
            if (self.vehicleCloneForEditing === null) {
                console.warn('No vehicle edited, cannot save')
                throw new Error('No vehicle edited, cannot save it')
            }
            self.saveStatus = OperationInProgress()

            const vehicleSnapshot = getSnapshot(self.vehicleCloneForEditing)
            if (vehicleSnapshot === null) {
                throw new Error('Empty vehicle snapshot!, cannot save it')
            }

            const vehicleJson = JSON.stringify(replaceChecklistReferences(vehicleSnapshot))
            
            const [url, method] = ExistingVehicleEditingCloneModel.is(self.vehicleCloneForEditing)
                ? [self.vehicleUrl(self.vehicleCloneForEditing.id), 'PATCH'] 
                : [self.getVehiclesUrl, 'POST'] 
    
            const saveResult = yield* toGenerator(self.loadObjectOrError<void>(
                url, 
                { method, body: vehicleJson},
                { fetchOperationName: 'Save vehicle'}
            ))

            if (saveResult.error) {
                self.saveStatus = OperationFailure(saveResult.error)    
                return false
            }

            self.saveStatus = OperationSuccess()
            // Refetch vehicle data
            yield self.fetchAndSetVehiclesAndMaintenance()
            return true
        }),
        toggleSelectedVehicleIsParked: flow(function* toggleSelectedVehicleIsParked() {
            if (self.selectedVehicle === null) {
                throw Error('No vehicle selected, cannot toggle parking status')
            }

            const vehicleState = self.vehicleStates.get(self.selectedVehicle.id.toString())
            if (!vehicleState){
                throw Error('No vehicle state for selected vehicle')
            }
            self.changeIsParkedStatus = OperationInProgress()
            
            const isParked = !vehicleState.isParked
            const vehicleJson = JSON.stringify({ id: self.selectedVehicle.id, is_parked: isParked })

            const markVehicleAsParked = yield* toGenerator(
                self.loadObjectOrError<void>(
                    self.getVehicleSetParkedStateUrl(self.selectedVehicle.id), 
                    { method: 'POST', body: vehicleJson },
                    { fetchOperationName: 'Toggle vehicle parked state'}
                )
            )
            if (markVehicleAsParked.error) {
                self.changeIsParkedStatus = OperationFailure(markVehicleAsParked.error)    
                return
            }
            self.changeIsParkedStatus = OperationSuccess()

            // Refetch vehicle data
            yield self.fetchVehiclesState()
            return
        }),
        toggleSelectedVehicleInService: flow(function* toggleSelectedVehicleInService() {
            if (self.selectedVehicle === null) {
                throw Error('No vehicle selected, cannot toggle service status')
            }

            const vehicleState = self.vehicleStates.get(self.selectedVehicle.id.toString())
            if (!vehicleState){
                throw Error('No vehicle state for selected vehicle')
            }

            self.changeInServiceStatus = OperationInProgress()

            const isInService = !vehicleState.isInService
            const vehicleJson = JSON.stringify({ id: self.selectedVehicle.id, is_in_service: isInService })

            const markVehicleInServiceState = yield* toGenerator(
                self.loadObjectOrError<void>(
                    self.getVehicleSetServiceStateUrl(self.selectedVehicle.id), 
                    { method: 'POST', body: vehicleJson },
                    { fetchOperationName: 'Set vehicle in service state'}
                )       
            )
            if (markVehicleInServiceState.error) {
                self.changeInServiceStatus = OperationFailure(markVehicleInServiceState.error)    
                return
            }
            self.changeInServiceStatus = OperationSuccess()

            // Refetch vehicle data
            yield self.fetchVehiclesState()
            return

        }),
        saveEditedPartInstance: flow(function* saveEditedPartInstance() {
            if (self.editedPartInstance === null) {
                throw Error(`Can't save edited `)
            }
            self.saveEditedPartInstanceStatus = OperationInProgress()

            const [url, method] = ExistingPartInstanceEditingCloneModel.is(self.editedPartInstance) ? 
                [self.getPartInstanceUrl(self.editedPartInstance.id), 'PATCH']:
                [self.getPartInstancesUrl, 'POST']
                
            const json = getSnapshot(self.editedPartInstance)
            
            const requestBody = JSON.stringify(
                {
                    part_number_id: json?.part_number, // fill part_number_id field as this is the field backend looks at
                    ...json,
                    creation_timestamp: json?.creation_timestamp?.split('Z')[0], // send timestamp in timezone-naive implied UTC format
                }    
            )
            const saveResult = yield* toGenerator(self.loadObjectOrError<void>(
                url,
                { method, body: requestBody},
                { fetchOperationName: 'Save part instance' }
            ))

            if (saveResult.error) {
                self.saveEditedPartInstanceStatus = OperationFailure(saveResult.error)
                return false
            }

            self.saveEditedPartInstanceStatus = OperationSuccess()
            if (self.selectedVehicle?.id !== undefined){
                void self.fetchVehicleParts(self.selectedVehicle.id)
                void self.fetchUnassignedPartInstances()
                void self.fetchAndSetVehiclesAndMaintenance()
            }
            return true
            
        })
    }
})

